import React from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
} from '@mui/material';

import {
  CiscoProductSelector,
  RagPipelineGroupSelector,
  RagPipelinePurposeSelector,
} from './RagSelects';

type ProductRagPipelineMappingPayload = {
  id?: number | string;
  product: number;
  purpose?: number | null;
  pipeline_group: number;
  priority: number;
  is_active: boolean;
  is_default: boolean;
  meta_filters: Record<string, any>;
  notes?: string;
  updated_by?: string;
};

type Props = {
  open: boolean;
  initialValue?: any | null;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (payload: ProductRagPipelineMappingPayload) => Promise<void>;
};

const idOf = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null;

  const id = typeof value === 'object' ? value.id ?? value.Id : value;
  const numeric = Number(id);

  return Number.isFinite(numeric) ? numeric : null;
};

const safeJsonStringify = (value: any) => {
  if (value === null || value === undefined || value === '') return '{}';

  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '{}';
  }
};

const ProductMappingEditorDialog: React.FC<Props> = ({
                                                       open,
                                                       initialValue,
                                                       saving,
                                                       onClose,
                                                       onSubmit,
                                                     }) => {
  const [form, setForm] = React.useState<any>({});
  const [metaFiltersText, setMetaFiltersText] = React.useState('{}');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;

    setForm({
      id: initialValue?.id,
      product: idOf(initialValue?.product ?? initialValue?.product_id),
      purpose: idOf(initialValue?.purpose ?? initialValue?.purpose_id),
      pipeline_group: idOf(initialValue?.pipeline_group ?? initialValue?.pipeline_group_id),
      priority: initialValue?.priority ?? 100,
      is_active: initialValue?.is_active ?? true,
      is_default: initialValue?.is_default ?? false,
      notes: initialValue?.notes ?? '',
      updated_by: initialValue?.updated_by ?? 'admin-ui',
    });

    setMetaFiltersText(safeJsonStringify(initialValue?.meta_filters || {}));
    setError(null);
  }, [open, initialValue]);

  const setField = (key: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [key]: value }));
  };

  const submit = async () => {
    setError(null);

    const productId = idOf(form.product);
    const pipelineGroupId = idOf(form.pipeline_group);
    const purposeId = idOf(form.purpose);

    if (!productId) {
      setError('Cisco Product is required.');
      return;
    }

    if (!pipelineGroupId) {
      setError('Pipeline Group is required.');
      return;
    }

    let metaFilters: Record<string, any> = {};

    try {
      metaFilters = metaFiltersText.trim() ? JSON.parse(metaFiltersText) : {};
      if (!metaFilters || typeof metaFilters !== 'object' || Array.isArray(metaFilters)) {
        throw new Error('Meta filters must be a JSON object.');
      }
    } catch (err: any) {
      setError(err?.message || 'Meta filters must be valid JSON.');
      return;
    }

    await onSubmit({
      id: form.id,
      product: productId,
      purpose: purposeId || null,
      pipeline_group: pipelineGroupId,
      priority: Number(form.priority || 100),
      is_active: Boolean(form.is_active),
      is_default: Boolean(form.is_default),
      meta_filters: {
        ...metaFilters,
        product_id: productId,
      },
      notes: form.notes || '',
      updated_by: form.updated_by || 'admin-ui',
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {form.id ? 'Edit Product RAG Mapping' : 'Add Product RAG Mapping'}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <CiscoProductSelector
            value={form.product}
            onChange={productId => setField('product', productId)}
            required
            helperText="Only Cisco products should be mapped to RAG pipelines."
          />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <RagPipelinePurposeSelector
                value={form.purpose}
                onChange={purposeId => setField('purpose', purposeId)}
                label="Purpose"
                helperText="Example: chat or doc_export. Blank means default."
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RagPipelineGroupSelector
                value={form.pipeline_group}
                onChange={groupId => setField('pipeline_group', groupId)}
                label="Pipeline Group"
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Priority"
                value={form.priority ?? 100}
                onChange={event => setField('priority', event.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(form.is_default)}
                    onChange={event => setField('is_default', event.target.checked)}
                  />
                }
                label="Default"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(form.is_active)}
                    onChange={event => setField('is_active', event.target.checked)}
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={6}
                label="Meta Filters"
                value={metaFiltersText}
                onChange={event => setMetaFiltersText(event.target.value)}
                helperText="JSON object merged into DRIFT meta_filters. product_id is automatically included."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Notes"
                value={form.notes || ''}
                onChange={event => setField('notes', event.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Updated By"
                value={form.updated_by || ''}
                onChange={event => setField('updated_by', event.target.value)}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductMappingEditorDialog;